import { useMemo } from "react"
import ShippingCalculationReadonlyModifier from "./shipping-calucation-readonly"
import { Button } from "@gsa/afp-component-library"
import { UploadDocumentationModal } from "./upload-documentation-modal"
import deleteDocumentationModal from "./delete-documentation-modal"
import AttachmentDownload from "../../../../../ReviewDetails/Attachment/AttachmentDownload/AttachmentDownload"
import { ReadonlyModifier, SelectControl } from "@gsa/afp-shared-form-utils"
import { useDispatch, useSelector } from 'react-redux';
import { emDashUnicode } from "../../../../../../constants/constants"
import { setVendorQuotes } from '../../../../../../reducers/leasing.jsx';
import { RequisitionStatus } from '../../../../../ReviewDetails/RequisitionDetailsUtils.jsx';

  const uploadTableColumns = [
    {
      Header: 'File name',
      accessor: 'filename',
      sortable: false,
      Cell: ({ row: { original } }) => {
        return (
          <AttachmentDownload
            name={original?.name}
            metadataId={original?.metadataId}
          />
        );
      },
    },
    {
      Header: 'Note',
      accessor: 'note',
      sortable: false,
      cellClassName: 'attachment-note',
      Cell: ({ row: { original } }) => {
        return original?.description || emDashUnicode;
      },
    },
  ];

export function generateAreqReviewDefaultValues({
    vendorQuote,
    index,
    vendors,
    makeModels,
    requisitionStatus
}) {
    const selectedVendorId = vendorQuote.vendorId || '-1';
    const selectedMakeModel = vendorQuote.contractLineId || '-1';
    return useMemo(() => ({
        [`vendor-${index}`]: selectedVendorId,
        [`makeModel-${index}`]: selectedMakeModel,
        [`quote-${index}`]: vendorQuote.quoteAttachment || null,
        [`contractingOfficerComments-${index}`]: vendorQuote.contractingOfficerComments || '',
        [`hasSupportingDocumentation-${index}`]: vendorQuote.vendorQuoteDocuments?.length > 0 || false,
        [`files-${index}`]: vendorQuote.vendorQuoteDocuments?.map((doc) => ({
            ...doc.requisitionAttachment,
            note: doc.requisitionAttachment.description || '',
            id: doc.requisitionAttachment.requisitionAttachmentId,
        })) || [],
        [`areqPricePerVehicle-${index}`]: vendorQuote.totalPrice || 0,
        [`extendedShippingDays-${index}`]: vendorQuote.extendedShippingDays || 0,
        [`shippingDays-${index}`]: {
            base: 0, 
            additional: 0,
            extended: vendorQuote.extendedShippingDays || 0,
        },
        [`id-${index}`]: vendorQuote.requisitionVendorQuoteId || '',
        [`requisitionVendorQuoteId-${index}`]: vendorQuote.requisitionVendorQuoteId || '',
        [`isAcceptable-${index}`]: requisitionStatus === RequisitionStatus.ENGINEERING_APPROVAL  ? '' : vendorQuote.isAcceptable ? 'acceptable' : 'unacceptable',
        [`engineeringComments-${index}`]: vendorQuote.engineerComments || '',
    }), []);
}

export function generateContractingReviewVendorForm({
    index,
    uploadQuoteModal,
    handleUpload,
    handleSave,
    handleDelete,
    uploading,
    deleting,
    saving,
    handleEditAction,
    handleDeleteAction,
    vendors,
    makeModels,
    vendorQuote,
    handleDeleteVendorQuote,
    engineeringApproval,
}) {
    const activeContracts = useSelector((state) => state.leasingReducer.compareSelect.activeContracts);
    const vendorQuotes = useSelector((state) => state.leasingReducer.vendorQuotes);
    const calculatedPriceBreakdown = useSelector((state) => state.leasingReducer.compareSelect.calculatedPriceBreakdown);
    const selectedContract = activeContracts.find((contract) => contract.id === vendorQuote.contractLineId);
    const selectedPriceBreakdown = calculatedPriceBreakdown.find((breakdown) => breakdown.contractLineId === vendorQuote.contractLineId);
    const selectedContractId = selectedContract ? selectedContract?.contract?.id : '-1';
    const dispatch = useDispatch();
    const content = {
        buttonControls: {
            submit: false,
            cancel: false,
        },
        sections: [
            {
                fields: [
                    {
                        id: `vendor-${index}`,
                        type: 'select',
                        label: 'Vendor',
                        required: true,
                        options: [
                            { label: '-select-', value: '-1' },
                            ...vendors
                        ]
                    },
                    {
                        id: `makeModel-${index}`,
                        label: 'Make/Model',
                        required: true,
                        component: (props, formContext) => {
                            const vendorId = formContext.watch(`vendor-${index}`) || vendorQuote.vendorId;

                            return SelectControl({
                                ...props,
                                className: [],
                            }, {
                                id: props.id,
                                options: [
                                    { label: '-select-', value: '-1' },
                                    ...(makeModels[vendorId] || []),
                                ],
                                element:{
                                    control: {
                                        onChange: (id) => {
                                            const selectedContract = activeContracts?.find((contract) => contract?.contract?.id === id);
                                            const tempVendorQuotes = structuredClone(vendorQuotes);
                                            const vendorQuoteIndex = tempVendorQuotes?.findIndex((quote) => quote?.requisitionVendorQuoteId === vendorQuote?.requisitionVendorQuoteId);
                                            if(vendorQuoteIndex > -1){
                                                tempVendorQuotes[vendorQuoteIndex].contractLineId = selectedContract?.id;
                                                dispatch(setVendorQuotes(tempVendorQuotes))
                                            }
                                        }
                                    }
                                }
                            });
                        }
                    }
                ]
            },
            {
                altStyle: 'alt-blue-border',
                element: {
                    section: {
                        style: {
                            padding: '20px'
                        }
                    }
                },
                fields: [
                    {
                        id: `quote-${index}`,
                        type: 'file-upload',
                        label: 'Upload vendor quote',
                        required: true,
                        component: (props, formContext) => {
                            const value = formContext.getValues(`quote-${index}`);

                            if (value) {
                                return (
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '45px', }}>
                                        <span><strong>{value.name}</strong></span>
                                        <div style={{ display: 'flex', gap: '10px', }}>
                                            <AttachmentDownload
                                                name='View'
                                                metadataId={value.metadataId}
                                                style={{ marginBottom: '10px' }}
                                            />
                                            <Button
                                                variant='unstyled'
                                                label='Delete'
                                                onClick={() => {
                                                    handleDeleteVendorQuote(value);
                                                }}
                                            />
                                        </div>
                                    </div>
                                )
                            }

                            return (
                                <Button
                                    style={{ marginTop: '45px' }}
                                    label='Upload file'
                                    leftIcon={{ name: 'plus' }}
                                    variant='outline'
                                    onClick={() => {
                                        uploadQuoteModal.openModal();
                                    }}
                                />
                                    
                            )
                        }
                    }
                ]
            },
            {
                fields: [
                    {
                        id: `contractingOfficerComments-${index}`,
                        type: 'textarea',
                        label: `Contracting officer's comments`,
                        element: {
                            control: {
                                maxLength: 100,
                                countdown: true,
                                autoGrow: true,
                                style: {
                                    maxWidth: '100%',
                                }
                            }
                        }
                    },
                    {
                        dependentOn: `files-${index}`,
                        dependentWaitType: 'disabled',
                        dependentValue: (val) => val?.length === 0,
                        id: `hasSupportingDocumentation-${index}`,
                        type: 'checkbox',
                        label: 'Supporting documents',
                    },
                    {
                        dependentOn: `hasSupportingDocumentation-${index}`,
                        dependentWaitType: 'hidden',
                        id: `files-${index}`,
                        type: 'file-table-upload',
                        element: {
                            control: {
                                tableHeader: <h3 style={{ margin: 0, padding: 0, }}>Supporting documentation</h3>,
                                uploadModal: UploadDocumentationModal,
                                uploadModalProps: { },
                                confirmDeleteModal: deleteDocumentationModal,
                                handleUpload,
                                handleSave,
                                handleDelete,
                                savingState: saving,
                                uploadingState: uploading,
                                deletingState: deleting,
                                tableActions: [
                                {
                                    icon: 'edit',
                                    label: 'Edit file documentation',
                                    handler: handleEditAction,
                                },
                                {
                                    icon: 'delete',
                                    label: 'Delete file',
                                    handler: handleDeleteAction,
                                },
                                ],
                                tableColumns: uploadTableColumns,
                                readonly: false,
                            }
                        }
                    },
                    {
                        id: `areqPricePerVehicle-${index}`,
                        type: 'currency',
                        label: 'AREQ price per vehicle',
                        required: true,
                        element: {
                            control: {
                                style: {
                                    maxWidth: '160px'
                                }
                            }
                        }
                    }
                ]
            },
            {
                fieldLayout: 'horizontal',
                gap: '100px',
                fields: [
                    {
                        id: `extendedShippingDays-${index}`,
                        type: 'number',
                        label: 'Extended shipping days',
                        required: false,
                        element: {
                            outerFieldWrapper: {
                                style: {
                                    maxWidth: '200px'
                                }
                            }
                        }
                    },
                    {
                        id: `shippingDays-${index}`,
                        component: (props, formContext) => {
                            const extendedShippingDays = parseInt(formContext.watch(`extendedShippingDays-${index}`)) || 0;

                            return ShippingCalculationReadonlyModifier({
                                base: selectedPriceBreakdown?.baseShippingDays || 0,
                                additional: selectedPriceBreakdown?.additionalShippingDays?.additionalShippingDays || 0,
                                extended: extendedShippingDays < 0 ? 0 : extendedShippingDays,
                            });
                        },
                    }
                ]
            },
        ]
    }

    return content
}

export function generateEngineeringApprovalVendorForm({
    index,
    uploadQuoteModal,
    handleUpload,
    handleSave,
    handleDelete,
    uploading,
    deleting,
    saving,
    handleEditAction,
    handleDeleteAction,
    vendors,
    makeModels,
    vendorQuote,
    handleDeleteVendorQuote,
    engineeringApproval,
    contractingApproval,
}) {const activeContracts = useSelector((state) => state.leasingReducer.compareSelect.activeContracts);
    const calculatedPriceBreakdown = useSelector((state) => state.leasingReducer.compareSelect.calculatedPriceBreakdown);
    const selectedContract = activeContracts.find((contract) => contract.id === vendorQuote.contractLineId);
    const selectedPriceBreakdown = calculatedPriceBreakdown.find((breakdown) => breakdown.contractLineId === vendorQuote.contractLineId);
    const selectedContractId = selectedContract ? selectedContract?.contract?.id : '-1';

    const content = {
        buttonControls: {
            submit: false,
            cancel: false,
        },
        sections: [
            {
                fieldLayout: 'horizontal',
                fields: [
                    {
                        id: `vendor-${index}`,
                        type: 'readonly',
                        label: 'Vendor',
                        options: [
                            { label: '-select-', value: '-1' },
                            ...vendors
                        ]
                    },
                    {
                        id: `makeModel-${index}`,
                        label: 'Make/Model',
                        component: (props, formContext) => {
                            const vendorId = formContext.watch(`vendor-${index}`) || vendorQuote.vendorId;
                            const makeModel = formContext.watch(`makeModel-${index}`);
                            return <div className='afp-form-field-readonly'>{makeModels[vendorId]?.find((model) => model.value === selectedContractId)?.label || emDashUnicode}</div>
                        }
                    },
                    {
                        id: `areqPricePerVehicle-${index}`,
                        type: 'readonly',
                        label: 'AREQ price per vehicle',
                        element: {
                            control: {
                                modifier: ReadonlyModifier.CurrencyModifier(),
                                style: {
                                    maxWidth: '160px'
                                }
                            }
                        }
                    }
                ]
            },
            {
                altStyle: 'alt-blue-border',
                element: {
                    section: {
                        style: {
                            padding: '20px'
                        }
                    }
                },
                fields: [
                    {
                        id: `quote-${index}`,
                        type: 'file-upload',
                        label: 'Vendor quote',
                        component: (props, formContext) => {
                            const value = formContext.getValues(`quote-${index}`);

                            if (value) {
                                return (
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '45px', }}>
                                        <div style={{ display: 'flex', gap: '10px', }}>
                                            <AttachmentDownload
                                                name={value.name}
                                                metadataId={value.metadataId}
                                                style={{ marginBottom: '10px' }}
                                            />
                                        </div>
                                    </div>
                                )
                            }

                            return emDashUnicode;
                        }
                    }
                ]
            },
            {
                gap: '40px',
                fields: [
                    {
                        id: `contractingOfficerComments-${index}`,
                        type: 'readonly',
                        label: `Contracting officer's comments`,
                        
                    },
                    {
                        label: 'Supporting documents',
                        id: `files-${index}`,
                        type: 'file-table-upload',
                        element: {
                            control: {
                                tableHeader: <h3 style={{ margin: 0, padding: 0, }}>Supporting documentation</h3>,
                                uploadModal: UploadDocumentationModal,
                                uploadModalProps: { },
                                confirmDeleteModal: deleteDocumentationModal,
                                handleUpload,
                                handleSave,
                                handleDelete,
                                savingState: saving,
                                uploadingState: uploading,
                                deletingState: deleting,
                                tableActions: [
                                {
                                    icon: 'edit',
                                    label: 'Edit file documentation',
                                    handler: handleEditAction,
                                },
                                {
                                    icon: 'delete',
                                    label: 'Delete file',
                                    handler: handleDeleteAction,
                                },
                                ],
                                tableColumns: uploadTableColumns,
                                readonly: true,
                            }
                        }
                    },
                ]
            },
            {
                fieldLayout: 'horizontal',
                gap: '100px',
                fields: [
                    {
                        id: `shippingDays-${index}`,
                        component: (props, formContext) => {
                            const extendedShippingDays = parseInt(formContext.watch(`extendedShippingDays-${index}`)) || 0;

                            return ShippingCalculationReadonlyModifier({
                                base: selectedPriceBreakdown?.baseShippingDays || 0,
                                additional: selectedPriceBreakdown?.additionalShippingDays?.additionalShippingDays || 0,
                                extended: extendedShippingDays < 0 ? 0 : extendedShippingDays,
                            });
                        },
                    }
                ]
            },
            {
                element: {
                    section: {
                        style: {
                            backgroundColor: '#fffbe8',
                            border: '1px solid #dfe1e2',
                            padding: '30px 40px',
                        }
                    }
                },
                fields: [
                    {
                        label: `Engineer's technical evaluation`,
                        type: contractingApproval ? 'readonly' : 'radio',
                        id: `isAcceptable-${index}`,
                        required: !contractingApproval,
                        options: [
                            { value: 'acceptable', label: 'Acceptable' },
                            { value: 'unacceptable', label: 'Unacceptable' },
                        ]
                    },
                    {
                        label: contractingApproval ? 'Comments' : 'Additional comments',
                        type: contractingApproval ? 'readonly' : 'textarea',
                        id: `engineeringComments-${index}`,
                        element: {
                            control: {
                                maxLength: 1000,
                                countdown: true,
                                autoGrow: true,
                                style: {
                                    maxWidth: '100%',
                                }
                            }
                        }
                    }
                ]
            }
        ]
    }

    return content
}